import React, { useState, useEffect } from 'react';
import DashboardLayout from '../layouts/DashboardLayout';
import api from '../api';
import './RagDebugger.css';

const RagDebugger = () => {
    // State for Ingestion Status
    const [ingestionStatus, setIngestionStatus] = useState({
        files_indexed: 0,
        last_updated: null,
        loading: true
    });

    // State for Simulation
    const [query, setQuery] = useState("");
    const [therapyType, setTherapyType] = useState("CBT"); // Default
    const [nResults, setNResults] = useState(50); // Increased default to show more results
    const [results, setResults] = useState([]);
    const [simLoading, setSimLoading] = useState(false);
    const [error, setError] = useState(null);

    // State for File Management
    const [fileList, setFileList] = useState([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [filterPending, setFilterPending] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default

    // [NEW] State for Overlay Testing
    const [overlayPersona, setOverlayPersona] = useState('family_pressure_childbearing');
    const [overlayClinicalState, setOverlayClinicalState] = useState('guilt,shame');
    const [overlayKeywords, setOverlayKeywords] = useState('family,expectation');
    const [overlayResults, setOverlayResults] = useState(null);
    const [overlayLoading, setOverlayLoading] = useState(false);
    const [overlayError, setOverlayError] = useState(null);
    const [showOverlay, setShowOverlay] = useState(false); // Collapsed by default

    // Initial Load - Get Status & Files (No Auto-Polling)
    useEffect(() => {
        fetchIngestionStatus();
        fetchFileList();
        // User requested no continuous API hits, so we rely on manual refresh.
    }, []);

    const fetchIngestionStatus = async () => {
        try {
            const response = await api.getIngestionStatus();
            setIngestionStatus({ ...response.data, loading: false });
        } catch (err) {
            console.error("Status fetch failed", err);
        }
    };

    const fetchFileList = async () => {
        setFilesLoading(true);
        try {
            const response = await api.listIngestedFiles();
            setFileList(response.data.files || []);
        } catch (err) {
            console.error("File list fetch failed", err);
        } finally {
            setFilesLoading(false);
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            await api.uploadKnowledgeFile(formData);
            fetchFileList(); // Refresh list
            fetchIngestionStatus(); // Refresh status
            alert("Upload successful! Ingestion started in background.");
        } catch (err) {
            setError("Upload failed: " + (err.response?.data?.error || err.message));
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (filename) => {
        if (!window.confirm(`Are you sure you want to delete ${filename}? This cannot be undone.`)) return;

        try {
            await api.deleteKnowledgeFile(filename);
            fetchFileList(); // Refresh list
            fetchIngestionStatus(); // Refresh stats
        } catch (err) {
            setError("Delete failed: " + (err.response?.data?.error || err.message));
        }
    };

    const filteredFileList = filterPending
        ? fileList.filter(f => !f.is_indexed || f.needs_reindex)
        : fileList;

    const handleSimulate = async (e) => {
        e.preventDefault();
        setSimLoading(true);
        setError(null);
        setResults([]);

        try {
            const response = await api.simulateRetrieval({
                query: query,
                therapy_type: therapyType,
                n_results: nResults
            });
            setResults(response.data.results || []);
        } catch (err) {
            console.error("Simulation failed", err);
            setError(err.response?.data?.error || "Simulation failed");
        } finally {
            setSimLoading(false);
        }
    };

    // [NEW] Overlay Testing Handler
    const handleTestOverlay = async (e) => {
        e.preventDefault();
        setOverlayLoading(true);
        setOverlayError(null);
        setOverlayResults(null);

        try {
            const response = await api.testOverlay({
                persona: overlayPersona ? overlayPersona.split(',').map(p => p.trim()) : [],
                clinical_state: overlayClinicalState ? overlayClinicalState.split(',').map(s => s.trim()) : [],
                keywords: overlayKeywords ? overlayKeywords.split(',').map(k => k.trim()) : []
            });
            setOverlayResults(response.data);
        } catch (err) {
            console.error("Overlay test failed", err);
            setOverlayError(err.response?.data?.error || "Overlay test failed");
        } finally {
            setOverlayLoading(false);
        }
    };

    const pendingCount = fileList.filter(f => !f.is_indexed || f.needs_reindex).length;

    const [showInfoPopup, setShowInfoPopup] = useState(false);

    return (
        <DashboardLayout>
            <div className="rag-debugger-container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
                <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100 border-b pb-2">🧠 RAG(Retrieval-Augmented Generation) Logic Debugger</h2>

                {/* Popup Modal */}
                {showInfoPopup && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
                        display: 'flex', justifyContent: 'center', alignItems: 'center'
                    }}>
                        <div style={{
                            backgroundColor: '#fff', padding: '30px', borderRadius: '8px',
                            maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)', position: 'relative'
                        }}>
                            <button
                                onClick={() => setShowInfoPopup(false)}
                                style={{
                                    position: 'absolute', top: '10px', right: '10px',
                                    border: 'none', background: 'none', fontSize: '1.5rem', cursor: 'pointer'
                                }}
                            >
                                &times;
                            </button>
                            <h2 style={{ marginTop: 0, color: '#2c3e50' }}>Understanding the Score</h2>

                            <div style={{ fontSize: '0.95rem', lineHeight: '1.6', color: '#34495e' }}>
                                <h3 style={{ color: '#e67e22', marginBottom: '10px' }}>Score: 1.5334 (Example)</h3>
                                <p>This number represents how "different" or "distant" the retrieved document is from your query. In this system, <strong>Lower is Better</strong>.</p>
                                <ul style={{ marginBottom: '20px' }}>
                                    <li><strong>0.0</strong> = Perfect Match (Identical content)</li>
                                    <li><strong>~0.5 - 0.7</strong> = Good Match (Semantically similar)</li>
                                    <li><strong>&gt; 1.0</strong> = Poor Match (Likely irrelevant)</li>
                                </ul>

                                <hr style={{ border: '0', borderTop: '1px solid #eee', margin: '20px 0' }} />

                                <h3 style={{ color: '#2980b9', marginBottom: '10px' }}>Breakdown: (Dist: 1.5334 - Bonus: 0.0000)</h3>
                                <p>The final score is calculated using this formula:</p>
                                <div style={{ backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px', textAlign: 'center', fontWeight: 'bold', marginBottom: '15px' }}>
                                    Score = Distance - Bonus
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                    <strong>1. Dist (vector distance):</strong>
                                    <p style={{ margin: '5px 0' }}>This measures meaning: "How far apart are these two ideas in concept space?" A high distance means the AI sees little relationship.</p>
                                </div>

                                <div>
                                    <strong>2. Bonus (reranking boost):</strong>
                                    <p style={{ margin: '5px 0' }}>This is a "boost" applied if the document matches the specific <strong>Therapy Framework</strong> you selected (CBT, REBT, SFBT). If key terms match (e.g. "dispute" for REBT), the score is lowered to rank it higher.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <div style={{ backgroundColor: '#ffdede', color: '#c0392b', padding: '10px', borderRadius: '4px', marginBottom: '20px' }}>
                        <strong>Error:</strong> {error}
                    </div>
                )}

                {/* Top Bar: Ingestion Status */}
                <div className="status-bar" style={{
                    backgroundColor: '#ecf0f1', padding: '15px', borderRadius: '8px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px'
                }}>
                    <div>
                        <strong>Knowledge Base Status: </strong>
                        {ingestionStatus.loading ? 'Checking...' : (
                            <span>
                                Indexed Chunks: <strong>{ingestionStatus.collection_count}</strong>
                                {ingestionStatus.current_file && (
                                    <span style={{ marginLeft: '15px', color: '#e67e22' }}>
                                        ⚙️ Currently Ingesting: <strong>{ingestionStatus.current_file}</strong>
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={async () => {
                                if (!window.confirm("Start background ingestion process? This may take time.")) return;
                                try {
                                    await api.triggerIngestion();
                                    alert("Ingestion started!");
                                    fetchIngestionStatus();
                                } catch (e) {
                                    alert("Error starting ingestion: " + e.message);
                                }
                            }}
                            style={{ padding: '5px 10px', cursor: 'pointer', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '4px' }}
                        >
                            ▶ Start Ingestion
                        </button>
                        <button onClick={() => { fetchIngestionStatus(); fetchFileList(); }} style={{ padding: '5px 10px', cursor: 'pointer' }}>Refresh All</button>
                    </div>
                </div>

                {/* New Section: File Management */}
                <div className="file-management-section" style={{ marginBottom: '30px', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h2 style={{ fontSize: '1.2rem', margin: 0 }}>📂 Knowledge Base Files</h2>
                            {/* Expand Toggle */}
                            <button
                                onClick={() => setIsExpanded(!isExpanded)}
                                style={{
                                    padding: '2px 8px', fontSize: '0.8rem', cursor: 'pointer',
                                    backgroundColor: 'transparent', border: '1px solid #aaa', borderRadius: '4px'
                                }}
                            >
                                {isExpanded ? '▼ Collapse' : '▶ Expand'}
                            </button>
                        </div>

                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem', color: '#555' }}>
                                <input
                                    type="checkbox"
                                    checked={filterPending}
                                    onChange={(e) => setFilterPending(e.target.checked)}
                                    style={{ marginRight: '5px' }}
                                />
                                Show Pending Only {filterPending && <strong>({pendingCount})</strong>}
                            </label>

                            <div style={{ position: 'relative' }}>
                                <input
                                    type="file"
                                    onChange={handleUpload}
                                    style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                                    disabled={uploading}
                                />
                                <button style={{
                                    padding: '8px 16px', backgroundColor: '#27ae60', color: 'white',
                                    border: 'none', borderRadius: '4px', pointerEvents: 'none'
                                }}>
                                    {uploading ? 'Uploading...' : '+ Upload New File'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Collapsible Content */}
                    {isExpanded && (
                        <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #eee' }}>
                            {filesLoading ? <div style={{ padding: '20px' }}>Loading files...</div> : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                    <thead style={{ backgroundColor: '#f8f9fa', sticky: 'top', top: 0 }}>
                                        <tr>
                                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Filename</th>
                                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Size (KB)</th>
                                            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Indexed?</th>
                                            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredFileList.map((file, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '8px 10px' }} title={file.path}>{file.filename}</td>
                                                <td style={{ padding: '8px 10px' }}>{(file.size_bytes / 1024).toFixed(1)} KB</td>
                                                <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                                    {file.is_indexed && !file.needs_reindex ? (
                                                        <span style={{ color: '#27ae60' }}>✔ Yes</span>
                                                    ) : (
                                                        <span style={{ color: '#f39c12' }}>⏳ Pending {file.needs_reindex ? '(Modified)' : ''}</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                                                    <button
                                                        onClick={() => handleDelete(file.filename)}
                                                        style={{ backgroundColor: '#e74c3c', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '3px', cursor: 'pointer' }}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredFileList.length === 0 && (
                                            <tr>
                                                <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                                    {filterPending ? "No pending files found." : "No files found in Knowledge Base."}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>

                {/* [NEW] India Cultural Overlay Testing Section */}
                <div className="overlay-testing-section" style={{ marginBottom: '30px', border: '2px solid #9b59b6', borderRadius: '8px', padding: '20px', backgroundColor: '#f8f5fb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h2 style={{ fontSize: '1.2rem', margin: 0, color: '#9b59b6' }}>🇮🇳 India Cultural Overlay Tester</h2>
                            <button
                                onClick={() => setShowOverlay(!showOverlay)}
                                style={{
                                    padding: '2px 8px', fontSize: '0.8rem', cursor: 'pointer',
                                    backgroundColor: 'transparent', border: '1px solid #9b59b6', borderRadius: '4px', color: '#9b59b6'
                                }}
                            >
                                {showOverlay ? '▼ Collapse' : '▶ Expand'}
                            </button>
                        </div>
                        <small style={{ color: '#7f8c8d' }}>Test cultural safety constraints for therapy responses</small>
                    </div>

                    {showOverlay && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '20px' }}>
                            {/* Left: Input Form */}
                            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '8px' }}>
                                <form onSubmit={handleTestOverlay}>
                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#9b59b6' }}>Persona (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={overlayPersona}
                                            onChange={(e) => setOverlayPersona(e.target.value)}
                                            placeholder="e.g. family_pressure_childbearing, domestic_violence_low"
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                        />
                                        <small style={{ color: '#7f8c8d', fontSize: '0.75rem' }}>E.g., family_pressure_childbearing, dowry_harassment</small>
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#9b59b6' }}>Clinical State (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={overlayClinicalState}
                                            onChange={(e) => setOverlayClinicalState(e.target.value)}
                                            placeholder="e.g. guilt, shame, fear"
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                        />
                                        <small style={{ color: '#7f8c8d', fontSize: '0.75rem' }}>E.g., guilt, shame, fear, panic</small>
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#9b59b6' }}>Keywords (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={overlayKeywords}
                                            onChange={(e) => setOverlayKeywords(e.target.value)}
                                            placeholder="e.g. family, expectation, pressure"
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                        />
                                        <small style={{ color: '#7f8c8d', fontSize: '0.75rem' }}>Optional keywords from patient text</small>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={overlayLoading}
                                        style={{
                                            width: '100%', padding: '12px', backgroundColor: overlayLoading ? '#d4a5d9' : '#9b59b6',
                                            color: 'white', border: 'none', borderRadius: '4px', cursor: overlayLoading ? 'not-allowed' : 'pointer',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {overlayLoading ? 'Testing...' : 'Test Overlay Rules'}
                                    </button>
                                </form>
                            </div>

                            {/* Right: Results */}
                            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '8px', maxHeight: '500px', overflowY: 'auto' }}>
                                <h3 style={{ fontSize: '1rem', marginTop: 0, color: '#9b59b6' }}>Overlay Results</h3>

                                {overlayError && (
                                    <div style={{ backgroundColor: '#fee', color: '#c00', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>
                                        <strong>Error:</strong> {overlayError}
                                    </div>
                                )}

                                {!overlayResults && !overlayLoading && (
                                    <div style={{ padding: '20px', textAlign: 'center', color: '#95a5a6' }}>
                                        Test the overlay engine to see matched rules and cultural safety constraints.
                                    </div>
                                )}

                                {overlayResults && overlayResults.metadata && (
                                    <div>
                                        <div style={{ backgroundColor: '#f0e6f6', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>
                                            <div style={{ marginBottom: '5px' }}>
                                                <strong>Rules Matched:</strong> {overlayResults.metadata.rules_matched}
                                            </div>
                                            <div style={{ marginBottom: '5px' }}>
                                                <strong>Rules Applied:</strong> {overlayResults.metadata.rules_applied}
                                            </div>
                                            <div style={{ marginBottom: '5px' }}>
                                                <strong>Confidence Multiplier:</strong> <span style={{ color: '#e67e22' }}>{overlayResults.confidence_multiplier}</span>
                                            </div>
                                            {overlayResults.metadata.rule_ids && overlayResults.metadata.rule_ids.length > 0 && (
                                                <div>
                                                    <strong>Rule IDs:</strong> {overlayResults.metadata.rule_ids.join(', ')}
                                                </div>
                                            )}
                                        </div>

                                        {overlayResults.matched_rules && overlayResults.matched_rules.length > 0 && (
                                            <div>
                                                <h4 style={{ fontSize: '0.9rem', color: '#9b59b6', marginTop: '15px' }}>Matched Rules:</h4>
                                                {overlayResults.matched_rules.map((rule, idx) => (
                                                    <div key={idx} style={{ backgroundColor: '#f9f9f9', padding: '10px', borderRadius: '4px', marginBottom: '10px', fontSize: '0.85rem' }}>
                                                        <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '5px' }}>
                                                            {rule.overlay_id} - {rule.risk_prevented}
                                                        </div>
                                                        <div style={{ fontSize: '0.8rem', color: '#555' }}>
                                                            <div><strong>Pacing:</strong> {rule.modification?.pacing}</div>
                                                            <div><strong>Tone:</strong> {rule.modification?.tone}</div>
                                                            {rule.modification?.avoid && rule.modification.avoid.length > 0 && (
                                                                <div style={{ marginTop: '5px' }}>
                                                                    <strong style={{ color: '#e74c3c' }}>Avoid:</strong> "{rule.modification.avoid[0]}"
                                                                </div>
                                                            )}
                                                            {rule.modification?.prefer && rule.modification.prefer.length > 0 && (
                                                                <div style={{ marginTop: '5px' }}>
                                                                    <strong style={{ color: '#27ae60' }}>Prefer:</strong> "{rule.modification.prefer[0]}"
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Simulation Area */}
                <div className="simulation-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '30px' }}>

                    {/* Left: Controls */}
                    <div className="controls-panel" style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
                        <h2 style={{ fontSize: '1.2rem', marginBottom: '15px' }}>Input Parameters</h2>
                        <form onSubmit={handleSimulate}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Patient Statement / Context</label>
                                <textarea
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="e.g. 'I feel completely hopeless and want to end it safely'"
                                    style={{ width: '100%', height: '100px', padding: '10px', borderRadius: '4px', border: '1px solid #bdc3c7' }}
                                    required
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Therapy Framework</label>
                                <select
                                    value={therapyType}
                                    onChange={(e) => setTherapyType(e.target.value)}
                                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #bdc3c7' }}
                                >
                                    <option value="CBT">CBT (Cognitive Behavioral)</option>
                                    <option value="REBT">REBT (Rational Emotive)</option>
                                    <option value="SFBT">SFBT (Solution Focused)</option>
                                    <option value="">None (Generic)</option>
                                </select>
                                <small style={{ color: '#7f8c8d' }}>
                                    Affects keyword bonuses (e.g. REBT prefers "must/should")
                                </small>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Number of Results</label>
                                <input
                                    type="number"
                                    value={nResults}
                                    onChange={(e) => setNResults(Math.max(1, Math.min(100, parseInt(e.target.value) || 20)))}
                                    min="1"
                                    max="100"
                                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #bdc3c7' }}
                                />
                                <small style={{ color: '#7f8c8d' }}>
                                    Number of chunks to retrieve and rank (default: 50, increased to catch more relevant results)
                                </small>
                            </div>

                            <button
                                type="submit"
                                disabled={simLoading}
                                style={{
                                    width: '100%', padding: '12px', backgroundColor: simLoading ? '#95a5a6' : '#3498db',
                                    color: 'white', border: 'none', borderRadius: '4px', cursor: simLoading ? 'not-allowed' : 'pointer',
                                    fontWeight: 'bold'
                                }}
                            >
                                {simLoading ? 'Simulating...' : 'Run Simulation'}
                            </button>
                        </form>
                    </div>

                    {/* Right: Results */}
                    <div className="results-panel">
                        <h2 style={{ fontSize: '1.2rem', marginBottom: '15px', display: 'flex', alignItems: 'center' }}>
                            Retrieval Results
                            <button
                                onClick={() => setShowInfoPopup(true)}
                                title="Click to understand scoring"
                                style={{
                                    marginLeft: '10px', backgroundColor: 'transparent', border: 'none',
                                    cursor: 'pointer', fontSize: '1.2rem'
                                }}
                            >
                                ℹ️
                            </button>
                            {results.length > 0 && <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#7f8c8d', marginLeft: 'auto' }}>(Sorted by Relevance)</span>}
                        </h2>

                        {results.length === 0 && !simLoading && (
                            <div style={{ padding: '40px', textAlign: 'center', color: '#95a5a6', border: '2px dashed #ecf0f1', borderRadius: '8px' }}>
                                Run a simulation to see the top retrieved chunks here.
                            </div>
                        )}

                        <div className="results-list" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {results.map((item, idx) => (
                                <div key={idx} className="result-card" style={{
                                    backgroundColor: '#fff', borderLeft: '5px solid #3498db',
                                    padding: '15px', borderRadius: '4px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                                }}>
                                    <div className="result-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '0.9rem' }}>
                                        <div>
                                            <span className="badge" style={{
                                                backgroundColor: '#3498db', color: 'white', padding: '2px 8px', borderRadius: '12px', marginRight: '10px'
                                            }}>
                                                Rank #{idx + 1}
                                            </span>
                                            <span style={{ fontWeight: 'bold', color: '#2c3e50' }}>
                                                {item.metadata.layer || 'Unknown Layer'}
                                            </span>
                                            <span style={{ color: '#7f8c8d', marginLeft: '10px' }}>
                                                {item.metadata.source} (Chunk {item.metadata.chunk_index})
                                            </span>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontWeight: 'bold', color: '#27ae60' }}>Score: {item.final_score.toFixed(4)}</div>
                                            <small style={{ color: '#95a5a6', display: 'block', marginTop: '2px' }}>
                                                Dist: {item.base_distance.toFixed(4)}
                                            </small>
                                            <small style={{ color: '#95a5a6', display: 'block' }}>
                                                Total Bonus: {item.rerank_bonus.toFixed(4)}
                                            </small>
                                            {(item.phrase_bonus > 0 || item.keyword_bonus > 0 || (item.therapy_bonus !== undefined && item.therapy_bonus > 0)) && (
                                                <small style={{ color: '#7f8c8d', display: 'block', fontSize: '0.75rem', marginTop: '2px' }}>
                                                    {item.has_exact_phrase && <span style={{ color: '#27ae60' }}>✓ Exact phrase</span>}
                                                    {item.phrase_bonus > 0 && <span> | Phrase: +{item.phrase_bonus.toFixed(3)}</span>}
                                                    {item.keyword_bonus > 0 && <span> | Keywords: +{item.keyword_bonus.toFixed(3)}</span>}
                                                    {item.therapy_bonus !== undefined && item.therapy_bonus > 0 && <span> | Therapy: +{item.therapy_bonus.toFixed(3)}</span>}
                                                    {item.matched_keywords !== undefined && <span> | Matched: {item.matched_keywords}</span>}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="result-content" style={{ backgroundColor: '#f9f9f9', padding: '10px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
                                        {item.text}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </DashboardLayout>
    );
};

export default RagDebugger;
